import React, {
    FunctionComponent,
    useEffect,
    useRef,
    useState,
  } from 'react';
  import { useTranslation } from 'react-i18next';
  import cloneDeep from 'lodash.clonedeep';
  import { RouteComponentProps, withRouter } from 'react-router-dom';
  import { connect, ConnectedProps } from 'react-redux';
  import { RootState } from 'data/root.reducers';
  import * as filtersSelectors from 'data/filters/filters.selectors';
  import * as globalSelectors from 'global/global-selectors';
  import * as actions from 'data/filters/filters.actions';
  import * as filterGroupActions from 'data/filterGroup/filterGroup.actions';
  import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
  import * as authenticationSelectors from 'data/authentication/authentication.selectors';
  import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
  import { FieldUpdateHandlerParam } from 'shared/form';
  import FormPanel from 'components/FormPanel';
  import FormButtons from 'components/FormButtons';
  import { Filters } from 'data/filters/filters.types';
  import { adaptTemplateForType, adaptTemplateOptions } from 'routes/Filters/utils/filtersTemplate.adapter';
  import { useFieldCreateNew } from 'routes/Filters/hooks/useFieldCreateNew';
  import FiltersForm, { FiltersFormRef } from '../../components/FiltersForm';
  import { filtersTemplate } from '../../templates/filtersTemplate';
  import getFiltersDataToSubmit from '../../utils/getFiltersDataToSubmit';

  type RouterParams = {
    history: string
  }

  const mapState = (state: RootState) => ({
    isOpen: filtersSelectors.isFiltersCreatePopupOpen(state),
    deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
    isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
    userId: authenticationSelectors.getAuthenticationUserId(state),
    userName: authenticationSelectors.getAuthenticationUserName(state),
    filterGroupList: filterGroupSelectors.getFilterGroupList(state),
  });

  const mapDispatch = {
    createFilters: actions.createFilters,
    closeFiltersCreatePage: actions.closeFiltersCreatePage,
    getFilterGroupList: filterGroupActions.getFilterGroupList,
  };

  const connector = connect(mapState, mapDispatch);

  type PropsFromRedux = ConnectedProps<typeof connector>;
  type PropsFromRouter = RouteComponentProps<RouterParams>

  export type Props = PropsFromRedux & PropsFromRouter;

  export const FiltersCreate: FunctionComponent<Props> = ({
    isOpen,
    deliveryFunctionId,
    history,
    isAuditEnabled,
    userId,
    userName,
    filterGroupList,
    createFilters,
    closeFiltersCreatePage,
    getFilterGroupList
  }: Props) => {
    const [template, setTemplate] = useState(filtersTemplate);
    const [formData, setFormData] = useState<Partial<Filters>>({});
    const filtersFormRef = useRef<FiltersFormRef>(null);
    const { t } = useTranslation();

    const [handleFieldCreateNew] = useFieldCreateNew(
      (field) => handleFieldUpdate(field),
    );

    useEffect(() => {
      getFilterGroupList();
    }, []);

    useEffect(() => {
      const adaptedTemplate = adaptTemplateOptions(filterGroupList, template);
      setTemplate(adaptedTemplate);
      handleFieldUpdate({
        name: 'type',
        value: 'IPADDRESS',
        label: undefined,
        valueLabel: undefined
      });
    }, [filterGroupList]);

    const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
      if (filtersTemplate == null || formData == null) {
        return;
      }
      const clonedFormData = cloneDeep(formData as Filters);
      const clonedTemplate = cloneDeep(adaptTemplateOptions(filterGroupList, filtersTemplate));
      const tcpFlags = ['ACK', 'FIN', 'PUSH', 'RST', 'SYN', 'URGENT'];
      const ipFlags = ['MORE_FRAGMENTS', 'DONT_FRAGMENT', 'RESERVED'];
      if (ipFlags.includes(fieldName)) {
        let ipFlagsValue = (formData.value !== undefined && formData.value !== 'IP-FLAGS')
                              ? formData.value?.split('&') : [];
        if (value) {
          ipFlagsValue.push(fieldName.replace('_', '-').toLowerCase());
        } else {
          ipFlagsValue = ipFlagsValue.filter((flag) => flag !== fieldName);
        }

        // override fieldName and value
        fieldName = 'value';
        value = ipFlagsValue.join('&');
      }
      if (tcpFlags.includes(fieldName)) {
        let tcpFlagsValue = (formData.value !== undefined && formData.value !== 'TCP-FLAGS')
                              ? formData.value?.split('&') : [];
        if (value) {
          tcpFlagsValue.push(fieldName);
        } else {
          tcpFlagsValue = tcpFlagsValue.filter((flag) => flag !== fieldName);
        }

        // override fieldName and value
        fieldName = 'value';
        value = tcpFlagsValue.join('&');
      }
      if (fieldName === 'type') {
        setFormData({
          ...clonedFormData,
          [fieldName]: value,
        });
        // override fieldName and value
        fieldName = 'value';
        value = adaptTemplateForType(clonedTemplate, value);
        setTemplate(clonedTemplate);
      }

      setFormData({
        ...clonedFormData,
        [fieldName]: value,
      });
    };

    const handleClickSave = () => {
      const isValid = filtersFormRef?.current?.isValid(true, true) ?? true;
      if (!isValid) {
        return;
      }

      const formValues = filtersFormRef?.current?.getValues();
      if (!formValues) {
        return;
      }
      if (formData.value && formData.value !== '') {
        formValues.value = formData.value;
      }

      const body = getFiltersDataToSubmit(
        formValues,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      );

      createFilters(deliveryFunctionId, body, history);
    };

    const handleClickCancel = () => closeFiltersCreatePage(history);

    const renderFormPanel = (formTitle: string) => {
      return (
        <FormPanel title={formTitle}>
          <FormButtons
            isEditing
            onClickCancel={handleClickCancel}
            onClickSave={handleClickSave}
            onClickClose={handleClickCancel}
          />
        </FormPanel>
      );
    };

    return (
      <div data-testid="FiltersCreate">
        {renderFormPanel(t('filters.create.title'))}
        <FiltersForm
          data={formData}
          template={template}
          ref={filtersFormRef}
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      </div>
    );
  };

  export default withRouter(connector(FiltersCreate));
